import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ColumnDef } from '@tanstack/react-table';
import { PageLayout } from '../components/common/PageLayout';
import { DataTable } from '../components/common/DataTable';
import { StatusBadge } from '../components/common/StatusBadge';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { useProcessRuns, ProcessRun } from '../hooks/useCaapApi';

const columns: ColumnDef<ProcessRun, any>[] = [
  { accessorKey: 'RunID', header: 'Run ID', size: 80 },
  {
    accessorKey: 'RunDate',
    header: 'Date',
    cell: ({ getValue }) => new Date(getValue()).toLocaleDateString(),
  },
  { accessorKey: 'RunType', header: 'Type' },
  { accessorKey: 'ScheduleType', header: 'Schedule' },
  {
    accessorKey: 'Status',
    header: 'Status',
    cell: ({ getValue }) => <StatusBadge status={getValue()} />,
  },
  { accessorKey: 'TotalAccountsFound', header: 'Accounts Found' },
  { accessorKey: 'TotalAccountsAssigned', header: 'Assigned' },
  {
    accessorKey: 'TotalPayoffBalance',
    header: 'Payoff Balance',
    cell: ({ getValue }) => {
      const val = getValue() as number;
      return val != null ? `$${val.toLocaleString('en-US', { minimumFractionDigits: 2 })}` : '\u2014';
    },
  },
  {
    accessorKey: 'DurationSeconds',
    header: 'Duration',
    cell: ({ getValue }) => {
      const s = getValue() as number | null;
      if (s == null) return '\u2014';
      return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`;
    },
  },
];

export const RunDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { data: runs, isLoading, error } = useProcessRuns();

  return (
    <PageLayout>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-primary">CAAP Run History</h1>
        <p className="mt-1 text-sm text-muted">
          Recent automated collections process runs
        </p>
      </div>

      {isLoading && <LoadingSpinner message="Loading runs..." />}
      {error && (
        <div className="rounded-md bg-red-50 dark:bg-red-950 p-4 text-sm text-red-700 dark:text-red-300">
          Failed to load runs: {(error as Error).message}
        </div>
      )}
      {runs && (
        <DataTable
          data={runs}
          columns={columns}
          onRowClick={(run) => navigate(`/caap/runs/${run.RunID}`)}
        />
      )}
    </PageLayout>
  );
};
